import { ControlPoint } from "@mui/icons-material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import {
  Box,
  Button,
  Card,
  Typography,
  useMediaQuery,
  useTheme,
  Snackbar,
  SnackbarContent,
} from "@mui/material";
import { useEffect, useState } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import NewClientModal from "../NewClientModal";
import {
  ButtonsContainer,
  StyledClientFieldContainer,
  StyledTitle,
} from "./styles";
import StyledTextField from "../StyledTextField";
import { formatDocument } from "src/utils/formatters";

interface SearchFormProps {
  customers: any[];
  onFilter?: (filtered: any[]) => void;
}

function SearchForm({ customers, onFilter }: SearchFormProps) {
  const [customer, setCustomer] = useState("");
  const [document, setDocument] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleOpen = () => setOpenModal(true);
  const handleClose = () => setOpenModal(false);

  const handleClear = () => {
    setCustomer("");
    setDocument("");
    setDataInicio("");
    setDataFim("");
    if (onFilter) onFilter(customers);
  };

  const handleDateChange = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {

    if (value.length > 10) return;
    const [year] = value.split("-");
    if (year && year.length > 4) return;

    setter(value);
  };

  useEffect(() => {
    const isUsingDateFilter = !!dataInicio || !!dataFim;

    if (isUsingDateFilter && !dataFim) return;

    if (dataInicio && dataFim && new Date(dataInicio) > new Date(dataFim)) {
      setErrorMsg("A data inicial não pode ser maior que a data final.");
      return;
    } else {
      setErrorMsg("");
    }

    const termName = customer.trim().toLowerCase();
    const termDoc = document.replace(/\D/g, "");
    const startDate = dataInicio ? new Date(dataInicio) : null;
    const endDate = dataFim ? new Date(dataFim) : null;

    const filtered = customers
      .map((c) => {
        const nameMatch = c.name.toLowerCase().includes(termName);
        const docMatch = c.document.replace(/\D/g, "").includes(termDoc);

        if (!c.equipment || c.equipment.length === 0) return null;

        const filteredEquipments = c.equipment.filter((eq: any) => {
          if (!eq.expiration_date) return false;
          const eqDate = new Date(eq.expiration_date.split("T")[0]);

          const afterStart = !startDate || eqDate >= startDate;
          const beforeEnd = !endDate || eqDate <= endDate;

          return afterStart && beforeEnd;
        });

        const finalEquipments =
          isUsingDateFilter && filteredEquipments.length > 0
            ? filteredEquipments
            : !isUsingDateFilter
              ? c.equipment
              : [];

        if (nameMatch && docMatch && finalEquipments.length > 0) {
          return { ...c, equipment: finalEquipments };
        }

        return null;
      })
      .filter(Boolean);

    if (onFilter) onFilter(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, document, dataInicio, dataFim, customers]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: "100%", maxWidth: 1230, mx: "auto", p: 1 }}>
        <Card sx={{ p: 2 }}>
          <StyledTitle>
            <FilterAltIcon />
            <Typography variant="h6">Filtros</Typography>
          </StyledTitle>

          <StyledClientFieldContainer>
            <StyledTextField
              label="Cliente"
              variant="outlined"
              size="small"
              fullWidth={isMobile}
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
              sx={{ flexGrow: 1 }}
            />

            <StyledTextField
              label="CNPJ/CPF"
              name="document"
              size="small"
              margin="normal"
              variant="outlined"
              value={document}
              onChange={(e) => {
                const masked = formatDocument(e.target.value);
                setDocument(masked);
              }}
              fullWidth={isMobile}
              sx={{ flexGrow: 1 }}
            />

            <StyledTextField
              label="Dt. Início"
              type="date"
              size="small"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              value={dataInicio}
              onChange={(e) => handleDateChange(e.target.value, setDataInicio)}
            />

            <StyledTextField
              label="Dt. Fim"
              type="date"
              size="small"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              value={dataFim}
              onChange={(e) => handleDateChange(e.target.value, setDataFim)}
            />

            <Button variant="outlined" color="primary" onClick={handleClear}>
              Limpar
            </Button>
          </StyledClientFieldContainer>

          <ButtonsContainer>
            <Button
              startIcon={<ControlPoint />}
              variant="contained"
              color="primary"
              onClick={handleOpen}
            >
              Novo Cliente
            </Button>
          </ButtonsContainer>
        </Card>

        <Snackbar
          open={!!errorMsg}
          autoHideDuration={4000}
          onClose={() => setErrorMsg("")}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <SnackbarContent
            message={errorMsg}
            sx={{ backgroundColor: "#d32f2f" }}
          />
        </Snackbar>

        <NewClientModal open={openModal} handleClose={handleClose} />
      </Box>
    </LocalizationProvider>
  );
}

export default SearchForm;
